import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { styled } from "@mui/material";
import { NumericQuestionLayout } from "./numericQuestionLayout";
import { NumericInput, UnitConverter } from "./numericInput";
import { PatientColorMap } from "../../../../layout/patientColorMap";
import { QuestionsIF } from "../../QuestionsIF";
import { useRangeRefinements } from "../rangeQuestionComponents/hooks/useRangeRefinements";
import { qualifierValueToQualifierFragment } from "../../../../utils/dataHelpers";
import clsx from "clsx";

//region [[ Styles ]]

export const GeneralNumericQuestionView = styled(NumericQuestionLayout)({
  "& .range-content": {
    display: "flex",
    flexDirection: "column",
    marginBottom: "8px",
  },
  "&.mini-view .range-content": {
    width: "calc(100% - 15px)",
    padding: "0 12px",
  },
});

export const SortNameWrapper = styled("div")({
  display: "flex",
  flexFlow: "row",
  width: "100%",
  alignItems: "center",
  justifyContent: "space-between",
});

export const ShortName = styled("div")({
  flexFlow: "row",
  alignItems: "center",
  fontSize: "12px",
  fontWeight: 500,
  marginBottom: "10px",
  justifyContent: "flex-start",
});

export const GeneralNumericInput = styled(NumericInput)({
  "&.general-numeric-input .numeric-input-view": {
    borderRadius: "14px",
    border: `1px solid ${PatientColorMap.DefaultDark}`,
    width: "100%",
    padding: "12px 14px",
    background: PatientColorMap.White,
    marginBottom: "7px",
    "& input, &:focus input, &:hover input": {
      border: "none",
    },
  },
  "&.mini-view .numeric-input-view": {
    padding: "6px 14px",
  },
});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface GeneralNumericQuestionProps extends QuestionsIF {
  placeholder?: string;
  converters: UnitConverter[];
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const GeneralNumericQuestion = ({
  answer,
  question,
  onAnswer,
  converters,
  miniView,
  ...props
}: GeneralNumericQuestionProps) => {
  const isAnswered = !!answer;

  const suggestion = question?.suggestions?.[0];

  const [answerValue, setAnswerValue] = useState(null);

  const { rangeItems, relation, abstractSuggestion } = useRangeRefinements({
    suggestion,
  });

  const handleValueSummited = useCallback(() => {
    if (answerValue) onAnswer([answerValue]);
  }, [onAnswer, answerValue]);

  const skip = useCallback(() => {
    const qualifiers = rangeItems.map((rangeItem) =>
      // Make sure to mark it as numeric so we don't lose the range as we assign the relation
      qualifierValueToQualifierFragment(rangeItem.value, relation, true),
    );

    const answerValue = [
      {
        ...abstractSuggestion,
        qualifiers,
        skip: true,
      },
    ];

    onAnswer(answerValue, true);
  }, [rangeItems, relation, abstractSuggestion, onAnswer]);

  useEffect(() => {
    if (answerValue && miniView) {
      handleValueSummited();
    }
  }, [answerValue, miniView, handleValueSummited]);

  return (
    <GeneralNumericQuestionView
      isAnswered={isAnswered}
      onSubmitClick={handleValueSummited}
      questionText={suggestion.question.text}
      onSkipClicked={skip}
      submitDisabled={!answerValue}
      miniView={miniView}
    >
      <SortNameWrapper>
        <ShortName>
          {miniView && suggestion.suggestion.feature.shortname}
        </ShortName>
        <GeneralNumericInput
          className={clsx("general-numeric-input", miniView ? "mini-view" : "")}
          onValidValue={setAnswerValue}
          onEnter={handleValueSummited}
          suggestion={suggestion}
          rangeItems={rangeItems}
          unitConverts={converters}
          placeholder={props.placeholder}
        />
      </SortNameWrapper>
    </GeneralNumericQuestionView>
  );
};
